import { useState } from 'react';
import { Menu } from 'lucide-react';
import clsx from 'clsx';
import { useScrolled } from '../hooks/use-scrolled';
import { useScrollspy } from '../hooks/use-scrollspy';
import { ITENS_NAV, TODAS_AS_SECOES, URL_APP } from '../dados/navegacao';
import { NavLinks } from './nav-links';
import { MenuMobile } from './menu-mobile';

/**
 * Header sticky: transparente no topo, ganha `backdrop-blur` e fundo
 * translúcido depois de 24px de scroll. Nav com scrollspy (link ativo
 * conforme a seção visível); CTA "Entrar" sempre aponta para a SPA, nunca
 * para uma âncora.
 */
export function Cabecalho(): JSX.Element {
  const rolado = useScrolled(24);
  const ativo = useScrollspy(TODAS_AS_SECOES);
  const [menuAberto, setMenuAberto] = useState(false);

  return (
    <header
      /* `casca-topo` + `data-rolado`: alvo das regras de toque do
         `estilos.css`, que trocam o backdrop-blur por fundo quase sólido —
         blur atrás do header é custo por frame de rolagem no celular. */
      data-rolado={rolado ? 'true' : 'false'}
      className={clsx(
        'casca-topo fixed inset-x-0 top-0 z-40 transition-colors duration-200',
        rolado
          ? 'bg-auth-fundo-1/70 border-auth-borda/8 border-b backdrop-blur-md'
          : 'border-b border-transparent bg-transparent',
      )}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
        {/* `-my-2.5 py-2.5`: a logo media 24px de alto como alvo de toque;
            o padding com margem negativa leva a 44px sem engordar o header. */}
        <a
          href="#inicio"
          className="text-auth-texto -my-2.5 flex items-center gap-2.5 py-2.5 text-base font-light tracking-tight"
        >
          <img
            src="/marca-specializer.png"
            alt=""
            width={206}
            height={86}
            className="h-auto w-9 shrink-0"
          />
          Specializer <span className="text-auth-marca-hover font-medium">Social</span>
        </a>

        <NavLinks
          itens={ITENS_NAV}
          ativo={ativo}
          className="hidden items-center gap-6 lg:flex"
        />

        <div className="flex items-center gap-3">
          <a
            href={URL_APP}
            className="border-auth-borda/15 text-auth-texto rounded-auth-controle hover:border-auth-borda/30 hidden border px-4 py-2 text-sm font-medium lg:inline-flex"
          >
            Entrar
          </a>
          {/*
            `size-11` (44px) é o alvo de toque, não o tamanho do ícone.

            O botão media 22×22 — menos da metade do mínimo confortável para o
            dedo, e o único caminho de navegação no celular. O ícone continua
            com 22; o que cresceu foi a área clicável, que é o que importa.
          */}
          <button
            type="button"
            aria-label="Abrir menu"
            onClick={() => setMenuAberto(true)}
            className="text-auth-texto -mr-2.5 inline-flex size-11 items-center justify-center lg:hidden"
          >
            <Menu size={22} aria-hidden />
          </button>
        </div>
      </div>

      <MenuMobile
        aberto={menuAberto}
        ativo={ativo}
        onFechar={() => setMenuAberto(false)}
      />
    </header>
  );
}
